import React from 'react';
import cn from 'classnames';
import * as Checkbox from '../Checkbox';

import arrow from './down-arrow.svg';

import * as styles from './MultiSelect.module.css';

type MultiSelectOption<ValueType> = {
  label: string;
  value: ValueType;
};

export type MultiSelectProps<ValueType = string> = {
  title: string;

  customClassName?: string;
  onChange: (value: ValueType, checked: boolean) => void;
  options: MultiSelectOption<ValueType>[];
  selectedOptions?: ValueType[];
};

export function MultiSelect({
  title,
  customClassName = '',
  onChange,
  options,
  selectedOptions,
}: MultiSelectProps) {
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleToggle = React.useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    } else if (buttonRef.current && buttonRef.current.contains(event.target as Node)) {
      setIsOpen((open) => !open);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('click', handleToggle, true);

    return () => {
      document.removeEventListener('click', handleToggle, true);
    };
  }, [handleToggle]);

  return (
    <div
      tabIndex={0}
      className={cn({
        [styles.dropdownWrapper]: true,
        [customClassName]: !!customClassName,
      })}
    >
      <div
        className={cn({
          [styles.dropdownButton]: true,
          [styles.active]: isOpen,
          [`${customClassName}--button`]: !!customClassName,
        })}
        ref={buttonRef}
      >
        {title}
        <img src={arrow} className={cn({ [styles.active]: isOpen })} alt="arrow" />
      </div>

      <div
        className={cn({
          [styles.dropdownContent]: true,
          [styles.showContent]: isOpen,
          [`${customClassName}--content`]: !!customClassName,
        })}
        ref={dropdownRef}
      >
        <div
          className={cn({
            [styles.innerDropdownButton]: true,
            [styles.innerButtonActive]: isOpen,
            [`${customClassName}--button`]: !!customClassName,
          })}
          onClick={() => setIsOpen(false)}
        >
          {title}
          <img src={arrow} className={cn({ [styles.innerButtonActive]: isOpen })} alt="arrow" />
        </div>

        <hr />

        <div className={styles.optionsBlock}>
          {options.map((option) => (
            <Checkbox.SingleCheckbox
              key={option.value}
              label={option.label}
              checked={!!selectedOptions?.includes(option.value)}
              value={option.value}
              onChange={(changed) => {
                onChange(option.value, changed);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
